import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import Header from "../components/Header";
import useAuth from "../hooks/useAuth";


const LoginUser = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { checkAuth, isAuthenticated, userType } = useAuth();

  // Redirection si déjà connecté (seulement après le chargement initial)
  useEffect(() => {
    if (!loading && isAuthenticated && userType === 'client') {
      navigate("/dashboard-user/echeances");
    }
  }, [isAuthenticated, userType, navigate, loading]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    console.log("🚀 Tentative de connexion pour l'email :", email);

    try {
      const response = await axios.post("https://api-ec.cinef-expertises.com/api/clients/login",{
          email,
          password
        }
      );
      console.log("📡 Réponse complète de l'API client:", response);
      console.log("📦 Données reçues:", response.data);

      // Vérifier la structure de la réponse
      if (!response.data) {
        throw new Error("Réponse API invalide - pas de données");
      }

      // Récupérer le token selon différentes structures possibles
      let token = null;
      if (response.data.token) {
        token = response.data.token;
        console.log("✅ Token client trouvé dans response.data.token:", token);
      } else if (response.data.access_token) {
        token = response.data.access_token;
        console.log("✅ Token client trouvé dans response.data.access_token:", token);
      } else if (response.data.data?.token) {
        token = response.data.data.token;
        console.log("✅ Token client trouvé dans response.data.data.token:", token);
      } else {
        console.error("❌ Structure de réponse:", Object.keys(response.data));
        throw new Error("Token non trouvé dans la réponse API");
      }

      if (!token) {
        throw new Error("Token manquant dans la réponse");
      }

      // Enregistrer le token
      console.log("💾 Enregistrement du token client:", token);
      localStorage.setItem("client_token", token);

      // Enregistrer les infos client si disponibles
      const client = response.data.client || response.data.data?.client || response.data.user;
      if (client) {
        console.log("👤 Infos client:", client);
        localStorage.setItem("client_info", JSON.stringify(client));
      }

      // Vérifier que le token a bien été enregistré
      const savedToken = localStorage.getItem("client_token");
      console.log("🔍 Vérification token client enregistré:", savedToken);

      if (savedToken !== token) {
        throw new Error("Erreur lors de l'enregistrement du token");
      }

      // Mettre à jour l'état d'authentification
      console.log("🔄 Mise à jour de l'état d'authentification");
      checkAuth();

      // Attendre un court instant pour que l'état soit mis à jour
      setTimeout(() => {
        console.log("🎯 Redirection vers dashboard client");
        navigate("/dashboard-user/echeances");
      }, 100);

    } catch (err) {
      console.error("💥 Erreur lors de la connexion client:", err);
      
      if (err.response) {
        console.error("📡 Erreur réponse API:", {
          status: err.response.status,
          data: err.response.data
        });
        
        if (err.response.status === 401) {
          setError("Identifiant ou mot de passe incorrect !");
        } else if (err.response.status === 422) {
          setError("Données de connexion invalides !");
        } else {
          setError(`Erreur serveur (${err.response.status}). Veuillez réessayer.`);
        }
      } else if (err.request) {
        console.error("📡 Erreur réseau:", err.request);
        setError("Problème de connexion. Vérifiez votre réseau.");
      } else {
        console.error("💥 Erreur:", err.message);
        setError(err.message || "Une erreur est survenue. Veuillez réessayer.");
      }
    } finally {
      setLoading(false);
    }
  };


  return (
    <>
      <Header/>
      <section className="login-page">
        <div className="decorative-circles">
          <div className="circle circle-1"></div>
          <div className="circle circle-2"></div>
          <div className="circle circle-3"></div>
        </div>

        <div className="content-grid">
          {/* Section gauche - Illustration */}
          <div className="illustration-section">
            {/* <div className="welcome-badge">
              <span className="badge-icon">🔐</span>
              <span className="badge-text">Connexion Sécurisée</span>
            </div> */}

            <h1 className="hero-title">
              Bienvenue dans votre
              <span className="title-gradient"> Espace Personnel</span>
            </h1>

            <p className="hero-description">
              Gérez vos documents comptables, consultez vos échéances et restez informé de l'avancement de vos dossiers en temps réel.
            </p>

            {/* Animation SVG intégrée */}
            <div className="illustration-box">
              <svg className="animated-svg" viewBox="0 0 500 400" xmlns="http://www.w3.org/2000/svg">
                {/* Fond */}
                <rect x="0" y="0" width="500" height="400" fill="url(#gradient1)" rx="20"/>
                
                {/* Définitions */}
                <defs>
                  <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{stopColor: '#008A44', stopOpacity: 0.1}} />
                    <stop offset="100%" style={{stopColor: '#dc2626', stopOpacity: 0.1}} />
                  </linearGradient>
                  
                  <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" style={{stopColor: '#008A44', stopOpacity: 1}} />
                    <stop offset="100%" style={{stopColor: '#00c767', stopOpacity: 1}} />
                  </linearGradient>
                  
                  <linearGradient id="gradient3" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" style={{stopColor: '#dc2626', stopOpacity: 1}} />
                    <stop offset="100%" style={{stopColor: '#ef4444', stopOpacity: 1}} />
                  </linearGradient>
                  
                  <filter id="shadow">
                    <feDropShadow dx="0" dy="4" stdDeviation="8" floodOpacity="0.2"/>
                  </filter>
                </defs>
                
                {/* Ordinateur */}
                <g className="computer-group">
                  <rect x="150" y="120" width="200" height="140" rx="8" fill="#1e293b" filter="url(#shadow)"/>
                  <rect x="160" y="130" width="180" height="110" rx="4" fill="#0f172a"/>
                  
                  {/* Écran avec animation */}
                  <g className="screen-content">
                    <rect x="170" y="145" width="70" height="8" rx="4" fill="url(#gradient2)">
                      <animate attributeName="width" values="70;160;70" dur="3s" repeatCount="indefinite"/>
                    </rect>
                    <rect x="170" y="165" width="120" height="6" rx="3" fill="#334155"/>
                    <rect x="170" y="180" width="90" height="6" rx="3" fill="#334155"/>
                    <rect x="170" y="195" width="130" height="6" rx="3" fill="#334155"/>
                    
                    {/* Icône de cadenas animé */}
                    <g className="lock-icon" transform="translate(290, 150)">
                      <rect x="0" y="15" width="30" height="35" rx="5" fill="url(#gradient3)"/>
                      <circle cx="15" cy="30" r="6" fill="#ffffff"/>
                      <rect x="13" y="30" width="4" height="10" rx="2" fill="#ffffff"/>
                      <path d="M 8 15 L 8 10 Q 8 0, 15 0 Q 22 0, 22 10 L 22 15" 
                            stroke="#dc2626" strokeWidth="3" fill="none"/>
                      <animateTransform
                        attributeName="transform"
                        type="rotate"
                        from="0 15 25"
                        to="10 15 25"
                        dur="2s"
                        repeatCount="indefinite"
                        values="0 15 25; 10 15 25; 0 15 25"
                        keyTimes="0; 0.5; 1"/>
                    </g>
                  </g>
                  
                  {/* Base de l'ordinateur */}
                  <rect x="230" y="260" width="40" height="8" rx="2" fill="#1e293b"/>
                  <rect x="180" y="268" width="140" height="12" rx="4" fill="#1e293b" filter="url(#shadow)"/>
                </g>
                
                {/* Éléments décoratifs animés */}
                <circle cx="80" cy="100" r="30" fill="url(#gradient2)" opacity="0.3">
                  <animate attributeName="r" values="30;35;30" dur="3s" repeatCount="indefinite"/>
                  <animate attributeName="opacity" values="0.3;0.5;0.3" dur="3s" repeatCount="indefinite"/>
                </circle>
                
                <circle cx="420" cy="300" r="25" fill="url(#gradient3)" opacity="0.3">
                  <animate attributeName="r" values="25;30;25" dur="4s" repeatCount="indefinite"/>
                  <animate attributeName="opacity" values="0.3;0.6;0.3" dur="4s" repeatCount="indefinite"/>
                </circle>
                
                {/* Particules flottantes */}
                <g className="particles">
                  <circle cx="100" cy="250" r="4" fill="#008A44" opacity="0.6">
                    <animate attributeName="cy" values="250;230;250" dur="5s" repeatCount="indefinite"/>
                  </circle>
                  <circle cx="380" cy="150" r="3" fill="#dc2626" opacity="0.6">
                    <animate attributeName="cy" values="150;130;150" dur="6s" repeatCount="indefinite"/>
                  </circle>
                  <circle cx="130" cy="180" r="5" fill="#00c767" opacity="0.5">
                    <animate attributeName="cy" values="180;160;180" dur="7s" repeatCount="indefinite"/>
                  </circle>
                </g>
                
                {/* Checkmarks animés */}
                <g className="checkmarks">
                  <circle cx="400" cy="100" r="18" fill="#008A44" opacity="0.9">
                    <animate attributeName="opacity" values="0.9;0.4;0.9" dur="2s" repeatCount="indefinite"/>
                  </circle>
                  <path d="M 393 100 L 398 105 L 408 93" stroke="#ffffff" strokeWidth="3" fill="none" strokeLinecap="round">
                    <animate attributeName="stroke-dasharray" values="0,100;100,0" dur="2s" repeatCount="indefinite"/>
                  </path>
                </g>
              </svg>
            </div>

            {/* <div className="features-list">
              <div className="feature-item">
                <span className="feature-icon">✓</span>
                <span>Accès 24/7 à vos documents</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">✓</span>
                <span>Suivi en temps réel</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">✓</span>
                <span>Notifications automatiques</span>
              </div>
            </div> */}
          </div>

          {/* Section droite - Formulaire */}
          <div className="form-section">
            <div className="form-card">
              <div className="form-header">
                
                <h2 className="form-title">Connexion Client</h2>
                <p className="form-subtitle">Accédez à votre espace personnel</p>
              </div>

              <form onSubmit={handleLogin}>
                <div className="form-group">
                  <label>Adresse email</label>
                  <div className="input-wrapper">
                    <span className="input-icon">📧</span>
                    <input
                      type="email"
                      className="form-control"
                      placeholder="votre.email@exemple.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Mot de passe</label>
                  <div className="input-wrapper">
                    <span className="input-icon">🔒</span>
                    <input
                      type={showPassword ? "text" : "password"}
                      className="form-control"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className="toggle-password"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={loading}
                    >
                      {showPassword ? "👁️" : "👁️‍🗨️"}
                    </button>
                  </div>
                </div>

                <div className="form-options">
                  <label className="checkbox-label">
                    <input type="checkbox" disabled={loading} />
                    <span>Se souvenir de moi</span>
                  </label>
                  <Link to="/forgot-password" className="forgot-link">
                    Mot de passe oublié ?
                  </Link>
                </div>

                {error && (
                  <div className="alert-error">
                    <span className="alert-icon">⚠️</span>
                    <span>{error}</span>
                  </div>
                )}

                <button type="submit" className="btn-submit" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner"></span>
                      <span>Connexion en cours...</span>
                    </>
                  ) : (
                    <>
                      <span>Se connecter</span>
                      <span className="btn-arrow">→</span>
                    </>
                  )}
                </button>
              </form>

              <div className="divider">
                <span>ou</span>
              </div>

              <p className="signup-text">
                Première visite ? Contactez votre expert-comptable pour obtenir vos accès 
              </p>
            </div>
          </div>
        </div>

        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
          }

          .login-page {
            min-height: 100vh;
            background: #ffffff;
            background-image: 
              radial-gradient(at 20% 30%, rgba(0, 138, 68, 0.03) 0%, transparent 50%),
              radial-gradient(at 80% 70%, rgba(220, 38, 38, 0.03) 0%, transparent 50%),
              radial-gradient(at 50% 50%, rgba(0, 138, 68, 0.02) 0%, transparent 50%);
            padding: 40px 20px;
            position: relative;
            overflow: hidden;
            padding-top: 120px;
          }

          .decorative-circles {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            overflow: hidden;
            opacity: 0.4;
          }

          .circle {
            position: absolute;
            border-radius: 50%;
          }

          .circle-1 {
            width: 600px;
            height: 600px;
            background: radial-gradient(circle, rgba(0, 138, 68, 0.08) 0%, transparent 70%);
            top: -300px;
            right: -200px;
            animation: float 25s infinite ease-in-out;
          }

          .circle-2 {
            width: 400px;
            height: 400px;
            background: radial-gradient(circle, rgba(220, 38, 38, 0.06) 0%, transparent 70%);
            bottom: -200px;
            left: -150px;
            animation: float 20s infinite ease-in-out reverse;
          }

          .circle-3 {
            width: 250px;
            height: 250px;
            background: radial-gradient(circle, rgba(0, 138, 68, 0.05) 0%, transparent 70%);
            top: 50%;
            left: 50%;
            animation: pulse 15s infinite ease-in-out;
          }

          @keyframes float {
            0%, 100% { transform: translateY(0) rotate(0deg); }
            50% { transform: translateY(-30px) rotate(10deg); }
          }

          @keyframes pulse {
            0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.05; }
            50% { transform: translate(-50%, -50%) scale(1.3); opacity: 0.1; }
          }

          .content-grid {
            max-width: 1400px;
            margin: 0 auto;
            display: grid;
            grid-template-columns: 1.2fr 1fr;
            gap: 60px;
            align-items: center;
            position: relative;
            z-index: 1;
          }

          /* Section Illustration */
          .illustration-section {
            padding: 40px;
            
          }

          .welcome-badge {
            display: inline-flex;
            align-items: center;
            gap: 10px;
            padding: 10px 20px;
            background: linear-gradient(135deg, rgba(0, 138, 68, 0.1), rgba(220, 38, 38, 0.1));
            border: 2px solid rgba(0, 138, 68, 0.2);
            border-radius: 50px;
            margin-bottom: 30px;
            animation: slideInLeft 0.6s ease-out;
          }

          .badge-icon {
            font-size: 20px;
          }

          .badge-text {
            font-weight: 600;
            font-size: 14px;
            color: #008A44;
            text-transform: uppercase;
            letter-spacing: 1px;
          }

          .hero-title {
            font-size: 52px;
            font-weight: 800;
            color: #1e293b;
            line-height: 1.2;
            margin-bottom: 24px;
            animation: slideInLeft 0.6s ease-out 0.1s backwards;
          }

          .title-gradient {
            background: linear-gradient(135deg, #008A44 0%, #dc2626 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            display: block;
            margin-top: 10px;
          }

          .hero-description {
            font-size: 18px;
            color: #64748b;
            line-height: 1.7;
            margin-bottom: 40px;
            animation: slideInLeft 0.6s ease-out 0.2s backwards;
          }

          .illustration-box {
            margin: 40px 0;
            animation: slideInLeft 0.6s ease-out 0.3s backwards;
          }

          .animated-svg {
            width: 100%;
            max-width: 500px;
            height: auto;
            filter: drop-shadow(0 20px 40px rgba(0, 0, 0, 0.1));
          }

          .features-list {
            display: flex;
            flex-direction: column;
            gap: 16px;
            margin-top: 40px;
            animation: slideInLeft 0.6s ease-out 0.4s backwards;
          }

          .feature-item {
            display: flex;
            align-items: center;
            gap: 12px;
            font-size: 16px;
            color: #475569;
          }

          .feature-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 28px;
            height: 28px;
            background: linear-gradient(135deg, #008A44, #00c767);
            color: white;
            border-radius: 50%;
            font-weight: 700;
            font-size: 14px;
            flex-shrink: 0;
          }

          /* Section Formulaire */
          .form-section {
            animation: slideInRight 0.6s ease-out;
          }

          .form-card {
            background: #ffffff;
            border-radius: 32px;
            padding: 56px 48px;
            box-shadow: 
              0 0 0 1px rgba(0, 0, 0, 0.03),
              0 2px 4px rgba(0, 0, 0, 0.02),
              0 12px 24px rgba(0, 0, 0, 0.04),
              0 24px 48px rgba(0, 0, 0, 0.06);
            border: 1px solid rgba(0, 0, 0, 0.04);
            transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .form-card:hover {
            box-shadow: 
              0 0 0 1px rgba(0, 0, 0, 0.04),
              0 4px 8px rgba(0, 0, 0, 0.03),
              0 16px 32px rgba(0, 0, 0, 0.06),
              0 32px 64px rgba(0, 0, 0, 0.08);
          }

          .form-header {
            text-align: center;
            margin-bottom: 40px;
          }

          .logo-badge {
            width: 80px;
            height: 80px;
            margin: 0 auto 24px;
            background: linear-gradient(135deg, rgba(0, 138, 68, 0.1), rgba(220, 38, 38, 0.1));
            border-radius: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 3px solid rgba(0, 138, 68, 0.2);
          }

          .form-title {
            font-size: 32px;
            font-weight: 800;
            color: #1e293b;
            margin-bottom: 8px;
          }

          .form-subtitle {
            font-size: 15px;
            color: #64748b;
          }

          .form-group {
            margin-bottom: 24px;
          }

          .form-group label {
            display: block;
            font-weight: 600;
            font-size: 14px;
            color: #334155;
            margin-bottom: 8px;
          }

          .input-wrapper {
            position: relative;
          }

          .input-icon {
            position: absolute;
            left: 16px;
            top: 50%;
            transform: translateY(-50%);
            font-size: 20px;
            opacity: 0.7;
          }

          .form-control {
            width: 100%;
            padding: 14px 16px 14px 50px;
            font-size: 15px;
            border: 2px solid #e2e8f0;
            border-radius: 12px;
            transition: all 0.3s ease;
            background: #f8fafc;
          }

          .form-control:focus {
            outline: none;
            border-color: #008A44;
            background: white;
            box-shadow: 0 0 0 4px rgba(0, 138, 68, 0.1);
          }

          .form-control:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .form-control::placeholder {
            color: #94a3b8;
          }

          .toggle-password {
            position: absolute;
            right: 16px;
            top: 50%;
            transform: translateY(-50%);
            background: none;
            border: none;
            cursor: pointer;
            font-size: 20px;
            opacity: 0.7;
            transition: opacity 0.2s;
          }

          .toggle-password:hover:not(:disabled) {
            opacity: 1;
          }

          .toggle-password:disabled {
            cursor: not-allowed;
            opacity: 0.4;
          }

          .form-options {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 28px;
            font-size: 14px;
          }

          .checkbox-label {
            display: flex;
            align-items: center;
            gap: 8px;
            cursor: pointer;
            color: #64748b;
          }

          .checkbox-label input[type="checkbox"] {
            width: 18px;
            height: 18px;
            cursor: pointer;
            accent-color: #008A44;
          }

          .checkbox-label input[type="checkbox"]:disabled {
            cursor: not-allowed;
            opacity: 0.5;
          }

          .forgot-link {
            color: #dc2626;
            text-decoration: none;
            font-weight: 600;
            transition: color 0.2s;
          }

          .forgot-link:hover {
            color: #ef4444;
            text-decoration: underline;
          }

          .alert-error {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 14px 16px;
            background: rgba(220, 38, 38, 0.1);
            border: 2px solid rgba(220, 38, 38, 0.2);
            border-radius: 12px;
            color: #dc2626;
            font-size: 14px;
            margin-bottom: 24px;
            animation: shake 0.5s;
          }

          .alert-icon {
            font-size: 20px;
            flex-shrink: 0;
          }

          @keyframes shake {
            0%, 100% { transform: translateX(0); }
            25% { transform: translateX(-10px); }
            75% { transform: translateX(10px); }
          }

          .btn-submit {
            width: 100%;
            padding: 16px;
            font-size: 16px;
            font-weight: 700;
            background: linear-gradient(135deg, #008A44 0%, #00c767 100%);
            color: white;
            border: none;
            border-radius: 12px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 12px;
            transition: all 0.3s ease;
            box-shadow: 0 4px 15px rgba(0, 138, 68, 0.3);
          }

          .btn-submit:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(0, 138, 68, 0.4);
          }

          .btn-submit:active:not(:disabled) {
            transform: translateY(0);
          }

          .btn-submit:disabled {
            opacity: 0.7;
            cursor: not-allowed;
            transform: none;
          }

          .btn-arrow {
            font-size: 20px;
            transition: transform 0.3s;
          }

          .btn-submit:hover:not(:disabled) .btn-arrow {
            transform: translateX(5px);
          }

          .spinner {
            width: 20px;
            height: 20px;
            border: 3px solid rgba(255, 255, 255, 0.3);
            border-top-color: #ffffff;
            border-radius: 50%;
            animation: spin 0.8s linear infinite;
          }

          @keyframes spin {
            to { transform: rotate(360deg); }
          }

          .divider {
            display: flex;
            align-items: center;
            text-align: center;
            margin: 32px 0;
            color: #94a3b8;
            font-size: 14px;
          }

          .divider::before,
          .divider::after {
            content: '';
            flex: 1;
            border-bottom: 1px solid #e2e8f0;
          }

          .divider span {
            padding: 0 16px;
            font-weight: 500;
          }

          .signup-text {
            text-align: center;
            color: #64748b;
            font-size: 14px;
            line-height: 1.6;
          }

          @keyframes slideInLeft {
            from {
              opacity: 0;
              transform: translateX(-30px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }

          @keyframes slideInRight {
            from {
              opacity: 0;
              transform: translateX(30px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }

          @media (max-width: 1024px) {
            .content-grid {
              grid-template-columns: 1fr;
              gap: 40px;
            }

            .illustration-section {
              text-align: center;
            }

            .hero-title {
              font-size: 42px;
            }

            .animated-svg {
              max-width: 400px;
            }
          }

          @media (max-width: 640px) {
            .form-card {
              padding: 32px 24px;
            }

            .hero-title {
              font-size: 32px;
            }

            .form-title {
              font-size: 26px;
            }

            .form-options {
              flex-direction: column;
              gap: 12px;
              align-items: flex-start;
            }
          }
        `}</style>
      </section>
    </>
  );
};

export default LoginUser;